import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ReactPaginate from "react-paginate";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { postData } from "../../utility/Utility";
import CustomLoader from "../../Components/CustomLoader/CustomLoader";
import PaymentDetailsModal from "../../Modal/PaymentDetailsModal/PaymentDetailsModal";
import moment from "moment";

function XOXOPaymentHistory() {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const rowsPerPage = 10;

  const fetchPaymentHistory = async (
    page = 1,
    search = "",
    startDate = "",
    endDate = ""
  ) => {
    setLoading(true);
    try {
      // Using the Xoxoday API format provided
      const response = await postData(
        "api/xoxoday/vouchers/admin-payment-report",
        {
          startDate: startDate || "2023-01-01",
          endDate: endDate || "2030-05-01",
          limit: rowsPerPage,
          page: page,
          search: search,
        }
      );

      if (response?.success && response?.data?.data?.getPaymentReport?.data) {
        const paymentData = response.data.data.getPaymentReport.data;
        setPayments(paymentData || []);
        setTotalPages(
          Math.ceil(
            (response.data.data.getPaymentReport.totalCount ||
              paymentData.length) / rowsPerPage
          )
        );
      } else {
        toast.error(response?.message || "Failed to fetch payment history");
        setPayments([]);
      }
    } catch (error) {
      toast.error("Error fetching payment history");
      console.error("Fetch payment history error:", error);
      // Set demo data for development
      setPayments([
        {
          invoice_number: "N/A",
          reference_id: "DEMO001",
          date: "2024-08-15",
          reason: "Brand vouchers issued",
          adjusted_amount: 5000,
          transaction_status: "Complete",
          closing_balance: 89948.99,
        },
      ]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPaymentHistory(currentPage + 1, searchTerm, startDate, endDate);
  }, [currentPage, searchTerm, startDate, endDate]);

  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
    fetchPaymentHistory(selected + 1, searchTerm, startDate, endDate);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(0);
    fetchPaymentHistory(1, searchTerm, startDate, endDate);
  };

  const handleReset = () => {
    setSearchTerm("");
    setStartDate("");
    setEndDate("");
    setCurrentPage(0);
    fetchPaymentHistory(1, "", "", "");
  };

  const handleViewPayment = (payment) => {
    setSelectedPayment(payment);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedPayment(null);
  };

  return (
    <div className="content-page">
      <ToastContainer position="top-right" autoClose={3000} />
      <CustomLoader loading={loading} />
      <div className="content">
        <div className="container-fluid">
          <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
            <div className="flex-grow-1">
              <h4 className="fs-18 fw-semibold m-0">XOXO Payment History</h4>
              <p className="fw-semibold my-2">Payment History Management</p>
            </div>
            <div className="d-flex gap-2">
              <button
                className="btn btn-secondary"
                onClick={() => navigate("/xoxo-dashboard")}
                disabled={loading}
              >
                <i className="mdi mdi-arrow-left me-1"></i>
                Back to Dashboard
              </button>
              <button
                className="btn btn-primary"
                onClick={() => navigate("/xoxo-order-history")}
                disabled={loading}
              >
                <i className="mdi mdi-history me-1"></i>
                Order History
              </button>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    <div className="row g-3 align-items-center">
                      <div className="col-md-12 col-lg-3">
                        <input
                          type="text"
                          className="form-control ps-4"
                          placeholder="Search by payment ID or description..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                      <div className="col-md-12 col-lg-2">
                        <input
                          type="date"
                          className="form-control"
                          placeholder="Start Date"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                        />
                      </div>
                      <div className="col-md-12 col-lg-2">
                        <input
                          type="date"
                          className="form-control"
                          placeholder="End Date"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                        />
                      </div>
                      <div className="col-md-12 col-lg-3 d-flex gap-2">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={loading}
                        >
                          <i className="mdi mdi-magnify me-1"></i>
                          Search
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={handleReset}
                          disabled={loading}
                        >
                          <i className="mdi mdi-refresh me-1"></i>
                          Reset
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>

          <div className="row mt-2">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  {loading ? (
                    <div className="text-center py-5">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>S.No</th>
                            <th>Invoice Number</th>
                            <th>Reference ID</th>
                            <th>Date</th>
                            <th>Reason</th>
                            <th>Adjusted Amount</th>
                            <th>Status</th>
                            <th>Closing Balance</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {payments.length > 0
                            ? payments.map((payment, index) => (
                                <tr key={payment.reference_id || index}>
                                  <td>
                                    {currentPage * rowsPerPage + index + 1}
                                  </td>
                                  <td>{payment.invoice_number || "N/A"}</td>
                                  <td>{payment.reference_id}</td>
                                  <td>
                                    {payment.date
                                      ? moment(payment.date).format(
                                          "DD/MM/YYYY"
                                        )
                                      : "-"}
                                  </td>
                                  <td>{payment.reason || "-"}</td>
                                  <td>
                                    ₹
                                    {payment.adjusted_amount?.toLocaleString(
                                      "en-IN"
                                    )}
                                  </td>
                                  <td>
                                    <span
                                      className={`badge text-capitalize bg-${
                                        payment.transaction_status ===
                                        "Complete"
                                          ? "success"
                                          : "secondary"
                                      }`}
                                    >
                                      {payment.transaction_status}
                                    </span>
                                  </td>
                                  <td>
                                    ₹
                                    {payment.closing_balance?.toLocaleString(
                                      "en-IN"
                                    )}
                                  </td>
                                  <td>
                                    <button
                                      className="btn btn-sm btn-outline-info"
                                      onClick={() => handleViewPayment(payment)}
                                      title="View Details"
                                    >
                                      <i className="mdi mdi-eye"></i>
                                    </button>
                                  </td>
                                </tr>
                              ))
                            : !loading && (
                                <tr>
                                  <td colSpan="9" className="text-center py-4">
                                    No payments found
                                  </td>
                                </tr>
                              )}
                        </tbody>
                      </table>

                      {totalPages > 1 && (
                        <ReactPaginate
                          previousLabel={
                            <i className="mdi mdi-chevron-left"></i>
                          }
                          nextLabel={<i className="mdi mdi-chevron-right"></i>}
                          breakLabel="..."
                          breakClassName="page-item"
                          breakLinkClassName="page-link"
                          pageCount={totalPages}
                          marginPagesDisplayed={2}
                          pageRangeDisplayed={5}
                          onPageChange={handlePageChange}
                          containerClassName="pagination pagination-rounded justify-content-end mt-3"
                          pageClassName="page-item"
                          pageLinkClassName="page-link"
                          previousClassName="page-item"
                          previousLinkClassName="page-link"
                          nextClassName="page-item"
                          nextLinkClassName="page-link"
                          activeClassName="active"
                          forcePage={currentPage}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Details Modal */}
      <PaymentDetailsModal
        show={showModal}
        onClose={handleCloseModal}
        paymentData={selectedPayment}
      />
    </div>
  );
}

export default XOXOPaymentHistory;
